/**
 * The four questions an owner actually asks, answered from the snapshot.
 *
 *   Who has stopped buying?   Who buys the most?   What is not selling?
 *   Are we up or down on last month?
 *
 * Everything here is derived from vouchers already on the device — no extra
 * extraction, and nothing that needs a server able to read the books.
 *
 * ONE RULE RUNS THROUGH ALL OF IT: never state a number the data cannot
 * support. Each function reports the WINDOW it measured over, because "no sales
 * in 90 days" means something quite different when the snapshot only holds 120
 * days of history — and a dormancy report that quietly counts the edge of its
 * own window as a dormant customer is worse than no report.
 */
import type { Snapshot, Voucher } from "@tally/shared";

import { indexGroups, isReceivable, isSalesLedger, money, sum } from "./groups.js";
import type { GroupIndex } from "./groups.js";
import { postedVouchers } from "./reports.js";

/** Inclusive span of voucher dates actually present, so callers can be honest. */
export type Window = { from: string; to: string; days: number };

export function windowOf(snapshot: Snapshot): Window {
  const dates = postedVouchers(snapshot).map((v) => v.date).sort();
  const from = dates[0] ?? "";
  const to = dates[dates.length - 1] ?? "";
  return { from, to, days: from && to ? daysBetween(from, to) : 0 };
}

function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / 86_400_000);
}

/**
 * Which customer a voucher belongs to.
 *
 * `partyLedger` is not always populated, so a debtor line is the fallback —
 * exactly as the redaction engine does it. A voucher with an empty party field
 * but a debtor entry is still that customer's invoice, and missing it would
 * report a live customer as dormant.
 */
function customerOf(voucher: Voucher, byName: Map<string, boolean>): string | null {
  if (voucher.partyLedger && byName.get(voucher.partyLedger)) return voucher.partyLedger;
  for (const entry of voucher.ledgerEntries) {
    if (byName.get(entry.ledger)) return entry.ledger;
  }
  return null;
}

function receivableLookup(snapshot: Snapshot, index: GroupIndex): Map<string, boolean> {
  return new Map(snapshot.ledgers.map((l) => [l.name, isReceivable(index, l)]));
}


const isSaleVoucher = (voucher: Voucher): boolean => /sale|invoice/i.test(voucher.voucherType);

/* ─────────────────────────────────────────────────── 1 · dormant customers ── */

export type DormantCustomer = {
  ledger: string;
  lastSale: string;
  daysSince: number;
  /** what they bought over the whole window — the reason to care */
  lifetimeValue: number;
  invoices: number;
  /** still owed, so a call can chase money and re-order in one go */
  balance: number;
};

export type DormantReport = {
  window: Window;
  threshold: number;
  customers: DormantCustomer[];
  /** customers seen buying at all, for the "17 of 58" framing */
  activeCount: number;
  /**
   * True when the threshold is longer than the history we hold, in which case
   * the list is meaningless and the screen says so instead of showing zero.
   */
  windowTooShort: boolean;
};

export const DORMANT_DAYS = 60;

export function deriveDormant(
  snapshot: Snapshot,
  asOf: string,
  index: GroupIndex = indexGroups(snapshot),
  thresholdDays: number = DORMANT_DAYS,
): DormantReport {
  const receivable = receivableLookup(snapshot, index);
  const window = windowOf(snapshot);

  type Row = { lastSale: string; value: number; invoices: number };
  const seen = new Map<string, Row>();

  for (const voucher of postedVouchers(snapshot)) {
    if (!isSaleVoucher(voucher)) continue;
    const customer = customerOf(voucher, receivable);
    if (!customer) continue;

    // Value the sale by its REVENUE lines, not the debtor line: the debtor
    // amount includes tax, and comparing customers on tax-inclusive figures
    // flatters whoever pays the higher rate.
    const revenue = Math.abs(
      sum(
        voucher.ledgerEntries
          .filter((e) => {
            const ledger = snapshot.ledgers.find((l) => l.name === e.ledger);
            return ledger ? isSalesLedger(index, ledger) : false;
          })
          .map((e) => e.amount),
      ),
    );

    const row = seen.get(customer) ?? { lastSale: "", value: 0, invoices: 0 };
    if (voucher.date > row.lastSale) row.lastSale = voucher.date;
    row.value = money(row.value + revenue);
    row.invoices += 1;
    seen.set(customer, row);
  }

  const balances = new Map(snapshot.ledgers.map((l) => [l.name, l.closingBalance]));

  const customers: DormantCustomer[] = [...seen.entries()]
    .map(([ledger, row]) => ({
      ledger,
      lastSale: row.lastSale,
      daysSince: daysBetween(row.lastSale, asOf),
      lifetimeValue: row.value,
      invoices: row.invoices,
      // Debit-negative in storage; a customer owing money reads positive here.
      balance: money(-(balances.get(ledger) ?? 0)),
    }))
    .filter((c) => c.daysSince >= thresholdDays)
    // Biggest customers first, not longest-silent first: a ₹9 lakh customer
    // quiet for 70 days is a more urgent call than a ₹900 one quiet for 300.
    .sort((a, b) => b.lifetimeValue - a.lifetimeValue);

  return {
    window,
    threshold: thresholdDays,
    customers,
    activeCount: seen.size,
    windowTooShort: window.days < thresholdDays,
  };
}

/* ──────────────────────────────────────────────── 2 · top parties and items ── */

export type RankedParty = { ledger: string; value: number; invoices: number; share: number };

export function deriveTopCustomers(
  snapshot: Snapshot,
  index: GroupIndex = indexGroups(snapshot),
  limit = 10,
): { rows: RankedParty[]; total: number; window: Window } {
  const receivable = receivableLookup(snapshot, index);
  const salesLedgers = new Set(
    snapshot.ledgers.filter((l) => isSalesLedger(index, l)).map((l) => l.name),
  );

  const byCustomer = new Map<string, { value: number; invoices: number }>();

  for (const voucher of postedVouchers(snapshot)) {
    if (!isSaleVoucher(voucher)) continue;
    const customer = customerOf(voucher, receivable);
    if (!customer) continue;

    const revenue = Math.abs(
      sum(voucher.ledgerEntries.filter((e) => salesLedgers.has(e.ledger)).map((e) => e.amount)),
    );
    if (revenue < 0.01) continue;

    const row = byCustomer.get(customer) ?? { value: 0, invoices: 0 };
    row.value = money(row.value + revenue);
    row.invoices += 1;
    byCustomer.set(customer, row);
  }

  const total = sum([...byCustomer.values()].map((r) => r.value));
  const rows = [...byCustomer.entries()]
    .map(([ledger, r]) => ({
      ledger,
      value: r.value,
      invoices: r.invoices,
      share: total > 0 ? (r.value / total) * 100 : 0,
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);

  return { rows, total, window: windowOf(snapshot) };
}

export type RankedItem = { item: string; qty: number; unit: string; value: number; share: number };

export function deriveTopItems(
  snapshot: Snapshot,
  limit = 10,
): { rows: RankedItem[]; total: number; window: Window } {
  const byItem = new Map<string, { qty: number; unit: string; value: number }>();

  for (const voucher of postedVouchers(snapshot)) {
    if (!isSaleVoucher(voucher)) continue;
    for (const line of voucher.inventoryEntries) {
      const row = byItem.get(line.stockItem) ?? { qty: 0, unit: line.unit, value: 0 };
      // Outward quantities are negative in Tally's convention.
      row.qty += Math.abs(line.qty);
      row.value = money(row.value + Math.abs(line.amount));
      byItem.set(line.stockItem, row);
    }
  }

  const total = sum([...byItem.values()].map((r) => r.value));
  const rows = [...byItem.entries()]
    .map(([item, r]) => ({
      item,
      qty: r.qty,
      unit: r.unit,
      value: r.value,
      share: total > 0 ? (r.value / total) * 100 : 0,
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);

  return { rows, total, window: windowOf(snapshot) };
}

/* ────────────────────────────────────────────────────────── 3 · dead stock ── */

export type DeadStockItem = {
  item: string;
  closingQty: number;
  closingValue: number;
  unit: string;
  /** null when it has never sold inside the window */
  lastSold: string | null;
  daysSince: number | null;
};

export type DeadStockReport = {
  window: Window;
  items: DeadStockItem[];
  /** capital sitting in items that did not sell at all */
  valueLockedUp: number;
  /** true when stock VALUES were withheld (a Sales role sees quantities only) */
  valuesWithheld: boolean;
};

/**
 * Stock still on the shelf that did not sell in the window.
 *
 * Counts SALES only. A stock journal moves an item between godowns or converts
 * it; treating that as movement would quietly clear genuinely dead items off
 * this list — and on the first real company stock journals are 108 of 502
 * vouchers, so the difference is not marginal.
 */
export function deriveDeadStock(snapshot: Snapshot): DeadStockReport {
  const lastSold = new Map<string, string>();

  for (const voucher of postedVouchers(snapshot)) {
    if (!isSaleVoucher(voucher)) continue;
    for (const line of voucher.inventoryEntries) {
      const previous = lastSold.get(line.stockItem) ?? "";
      if (voucher.date > previous) lastSold.set(line.stockItem, voucher.date);
    }
  }

  const holding = snapshot.stockItems.filter((item) => item.closingQty > 0.0001);
  const items: DeadStockItem[] = holding
    .filter((item) => !lastSold.has(item.name))
    .map((item) => ({
      item: item.name,
      closingQty: item.closingQty,
      closingValue: Math.abs(item.closingValue),
      unit: item.baseUnit,
      lastSold: null,
      daysSince: null,
    }))
    .sort((a, b) => b.closingValue - a.closingValue || b.closingQty - a.closingQty);

  return {
    window: windowOf(snapshot),
    items,
    valueLockedUp: sum(items.map((i) => i.closingValue)),
    // A Sales role gets quantities with values zeroed. Reporting "₹0 locked up"
    // as though the stock were worthless would be a lie of omission.
    valuesWithheld: holding.length > 0 && holding.every((i) => i.closingValue === 0),
  };
}

/* ───────────────────────────────────────────────────── 4 · month on month ── */

export type MonthOnMonth = {
  thisMonth: number;
  lastMonth: number;
  /** null when last month is zero — a percentage against nothing is noise */
  changePercent: number | null;
  thisLabel: string;
  lastLabel: string;
  /**
   * True when `asOf` is mid-month, so the comparison is a part-month against a
   * whole one. Without saying so, every report before the 28th looks like a
   * collapse.
   */
  partialMonth: boolean;
  dayOfMonth: number;
  daysInMonth: number;
};

export function deriveMonthOnMonth(
  snapshot: Snapshot,
  asOf: string,
  index: GroupIndex = indexGroups(snapshot),
): MonthOnMonth {
  const salesLedgers = new Set(
    snapshot.ledgers.filter((l) => isSalesLedger(index, l)).map((l) => l.name),
  );

  const [yearText, monthText, dayText] = asOf.split("-");
  const year = Number(yearText);
  const month = Number(monthText);
  const thisKey = `${yearText}-${monthText}`;
  const lastDate = new Date(Date.UTC(year, month - 2, 1));
  const lastKey = `${lastDate.getUTCFullYear()}-${String(lastDate.getUTCMonth() + 1).padStart(2, "0")}`;

  let thisMonth = 0;
  let lastMonth = 0;

  for (const voucher of postedVouchers(snapshot)) {
    const key = voucher.date.slice(0, 7);
    if (key !== thisKey && key !== lastKey) continue;

    const revenue = Math.abs(
      sum(voucher.ledgerEntries.filter((e) => salesLedgers.has(e.ledger)).map((e) => e.amount)),
    );
    if (revenue < 0.01) continue;

    if (key === thisKey) thisMonth = money(thisMonth + revenue);
    else lastMonth = money(lastMonth + revenue);
  }

  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const dayOfMonth = Number(dayText);

  return {
    thisMonth,
    lastMonth,
    changePercent: lastMonth > 0.01 ? ((thisMonth - lastMonth) / lastMonth) * 100 : null,
    thisLabel: monthLabelOf(thisKey),
    lastLabel: monthLabelOf(lastKey),
    partialMonth: dayOfMonth < daysInMonth,
    dayOfMonth,
    daysInMonth,
  };
}

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function monthLabelOf(key: string): string {
  const [year, month] = key.split("-");
  return `${MONTHS[Number(month) - 1] ?? month} ${String(year).slice(2)}`;
}
